/**
 * La primera parada de quien entró con una contraseña temporal.
 *
 * `SessionProvider` la renderiza EN LUGAR de toda la aplicación mientras la
 * sesión diga `must_change_password`: no hay adónde navegar para esquivarla.
 * Aun así, esta pantalla es cortesía — la garantía real es el 403
 * `password_change_required` con que el Worker responde a toda la API — pero
 * es la cortesía la que hace obvio qué toca hacer.
 *
 * El endpoint de cambio exige la contraseña actual (no se relajó esa regla:
 * seguiría exigiéndola en cualquier otro lado que llame a `changePassword`).
 * Lo que cambió es que ya no hace falta que la persona la vuelva a escribir:
 * `login()` la guarda en `sessionStorage` cuando la sesión nace con
 * `must_change_password`, y acá se consume una sola vez. Si no hay nada
 * guardado —modo privado, u otra pestaña— o si el Worker igual la rechaza,
 * cae al campo manual de siempre.
 */

import { useState, type FormEvent } from 'react';
import { passwordRule } from '@cleanblue/shared';
import { api, ApiError } from '@/lib/api';
import { consumeTempPassword, homeFor, useSession, useUser } from '@/lib/session';
import { Field, Note, PasswordInput } from '@/components/ui';
import { AuthShell } from './LoginPage';

export function CambiarContrasena() {
  const user = useUser();
  const { logout } = useSession();

  // Se lee una sola vez, al montar: es de un solo uso y `consumeTempPassword`
  // ya la borra de `sessionStorage` al leerla.
  const [captured] = useState(() => consumeTempPassword());
  const [manualCurrent, setManualCurrent] = useState('');
  const [needsManualCurrent, setNeedsManualCurrent] = useState(captured == null);

  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const current = needsManualCurrent ? manualCurrent : (captured ?? '');
  const rule = passwordRule.safeParse(next);
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready = current.length > 0 && rule.success && next === repeat;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      // Recarga completa hacia el home del rol, como hace el logout: el
      // navegador vuelve a pedir la sesión —ya sin el flag— y la aplicación
      // arranca limpia, sin rastro del estado acotado en ninguna caché.
      window.location.replace(homeFor(user.role));
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : new ApiError('unknown', 'Algo salió mal', 500);
      setError(apiErr);
      // La temporal guardada no sirvió (rara, pero posible: se generó una
      // nueva en otra pestaña, por ejemplo). Se muestra el campo para que la
      // persona la escriba a mano en vez de quedar trabada sin explicación.
      if (apiErr.code === 'invalid_credentials') setNeedsManualCurrent(true);
      setBusy(false);
    }
  }

  const firstName = user.full_name.split(' ')[0] ?? user.full_name;

  return (
    <AuthShell
      title="Elegí tu contraseña"
      subtitle={<>Hola, <strong>{firstName}</strong></>}
    >
      {error && <Note tone="danger">{error.message}</Note>}

      <form onSubmit={submit} className="mt-2">
        {needsManualCurrent && (
          <Field label="Contraseña temporal" error={error?.fieldError('current_password')}>
            <PasswordInput
              autoComplete="current-password"
              autoFocus
              value={manualCurrent}
              onChange={setManualCurrent}
              required
            />
          </Field>
        )}

        <Field
          label="Tu contraseña nueva"
          hint="Mínimo 8 caracteres."
          error={error?.fieldError('new_password') ?? (next.length > 0 && !rule.success ? rule.error.issues[0]?.message : undefined)}
        >
          <PasswordInput
            autoComplete="new-password"
            autoFocus={!needsManualCurrent}
            value={next}
            onChange={setNext}
            required
            minLength={8}
          />
        </Field>

        <Field label="Repetila" error={mismatch ? 'No coinciden todavía' : undefined}>
          <PasswordInput
            autoComplete="new-password"
            value={repeat}
            onChange={setRepeat}
            required
          />
        </Field>

        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={!ready || busy}>
          {busy ? 'Guardando…' : 'Guardar y entrar'}
        </button>
      </form>

      {/* La salida de emergencia: quien no tenga la temporal a mano puede salir
          y pedir otra, en vez de quedar mirando una pantalla sin puertas. */}
      <div className="mt-3" style={{ textAlign: 'center' }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void logout()}>
          Salir sin cambiarla
        </button>
      </div>
    </AuthShell>
  );
}
